/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks\useExtensionQuickList.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Extensions module.
────────────────────────────────────────────────────────────────*/


import { useState, useCallback, useEffect, useRef } from 'react';
import { fetchExtensions } from '../api/extensionsApi';

interface UseExtensionQuickListOptions {
    isOpen: boolean;
}

export function useExtensionQuickList({ isOpen }: UseExtensionQuickListOptions) {
    const [quickListExtensions, setQuickListExtensions] = useState<any[]>([]);
    const [initialExtensions, setInitialExtensions] = useState<any[]>([]);
    const [quickListSearch, setQuickListSearch] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [isLoadingInitial, setIsLoadingInitial] = useState(false);
    const loadingRef = useRef(false);

    const loadInitialExtensions = useCallback(async () => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        setIsLoadingInitial(true);
        try {
            const result = await fetchExtensions({ page: 1, limit: 100, sortBy: 'extension', sortOrder: 'asc' });
            setInitialExtensions(result.data);
            setQuickListExtensions(result.data);
        } catch {
            setInitialExtensions([]);
            setQuickListExtensions([]);
        } finally {
            setIsLoadingInitial(false);
            loadingRef.current = false;
        }
    }, []);

    const loadSearchResults = useCallback(async (search: string) => {
        const normalized = search.trim();
        if (normalized && normalized.length < 2) { setQuickListExtensions(initialExtensions); return; }
        setIsSearching(true);
        try {
            const result = await fetchExtensions({ page: 1, limit: 50, sortBy: 'extension', sortOrder: 'asc', search: normalized });
            setQuickListExtensions(result.data);
        } catch { setQuickListExtensions([]); }
        finally { setIsSearching(false); }
    }, [initialExtensions]);

    // Load on open
    useEffect(() => {
        if (isOpen && initialExtensions.length === 0 && !loadingRef.current) {
            void loadInitialExtensions();
        }
    }, [isOpen, initialExtensions.length, loadInitialExtensions]);

    // Debounced search
    useEffect(() => {
        if (!isOpen) { setQuickListSearch(''); return; }
        const normalized = quickListSearch.trim();
        if (!normalized || normalized.length < 2) { setQuickListExtensions(initialExtensions); return; }
        const timer = setTimeout(() => void loadSearchResults(quickListSearch), 800);
        return () => clearTimeout(timer);
    }, [quickListSearch, isOpen, loadSearchResults, initialExtensions]);

    return { quickListExtensions, quickListSearch, setQuickListSearch, isSearching, isLoadingInitial };
}
